/* ─────────────────────────────────────────────
   Flipbook — section de landing page (variante B), portage Shopify

   Trois contraintes qui dictent le code :

   1. Poids. Les 64 pages pèsent ~9,5 Mo. Rien n'est chargé au chargement
      de la page hôte : seule la couverture (page-01) est visible. Le
      flipbook s'initialise au premier clic sur le teaser.
   2. Fenêtre glissante. Même après ouverture, on ne pose le `src` que
      sur les pages proches de la position courante. StPageFlip masque
      les pages hors écran, donc `loading="lazy"` du navigateur ne se
      déclencherait jamais — la fenêtre est gérée à la main.
   3. Mesure. Le programme welcome-social-book se pilote sur le clic
      (voir PROGRAMME-OPTIMISATION-WELCOME-SOCIAL-BOOK.md §1), donc
      chaque interaction pousse un événement dans `window.dataLayer`.

   Les deux bibliothèques suivent la même règle : StPageFlip (44 Ko) est
   injecté au clic sur le teaser, PhotoSwipe (244 Ko) au premier zoom.
   Au chargement de la page hôte, cette section coûte ~4 Ko de script.

   Portage Shopify : les URL vendor et des 64 pages ne se déduisent plus
   d'une convention de chemin relatif (pas de dossier vendor/ ni pages/
   dans assets/, tout est à plat et hashé par asset_url). Elles sont
   injectées par le Liquid via data-attributes sur la section :
     data-lib-pageflip, data-lib-pswp-core, data-lib-pswp-lightbox
     data-pages-json (tableau JSON des 64 URL déjà résolues)
   ───────────────────────────────────────────── */

const TOTAL_PAGES = 64;
const IMAGE_WIDTH = 900;
const IMAGE_HEIGHT = 1126;

// Nombre de pages gardées chargées de part et d'autre de la position courante.
const WINDOW_BEHIND = 2;
const WINDOW_AHEAD = 4;

// GIF transparent 1×1 : évite une requête réseau sur les pages pas encore chargées.
const BLANK = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

const root = document.querySelector("[data-msb-flipbook]");
if (root) init(root);

// Injecte un script classique une seule fois et résout quand il est prêt.
let pageFlipLib = null;
function loadPageFlip(libUrl) {
  if (pageFlipLib) return pageFlipLib;
  pageFlipLib = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = libUrl;
    s.onload = () => resolve(window.St);
    s.onerror = () => reject(new Error("page-flip introuvable"));
    document.head.appendChild(s);
  });
  return pageFlipLib;
}

let pswpLib = null;
function loadPhotoSwipe(coreUrl, lightboxUrl) {
  if (pswpLib) return pswpLib;
  pswpLib = Promise.all([
    import(/* webpackIgnore: true */ lightboxUrl),
    import(/* webpackIgnore: true */ coreUrl),
  ]).then(([lightbox, core]) => ({
    PhotoSwipeLightbox: lightbox.default,
    PhotoSwipe: core.default,
  }));
  return pswpLib;
}

function init(root) {
  const libPageFlipUrl = root.dataset.libPageflip;
  const libPswpCoreUrl = root.dataset.libPswpCore;
  const libPswpLightboxUrl = root.dataset.libPswpLightbox;
  const pageUrls = JSON.parse(root.dataset.pagesJson || "[]");
  const pageUrl = (i) => pageUrls[i];

  const teaser = root.querySelector("[data-fb-teaser]");
  const bookEl = root.querySelector("[data-fb-book]");
  const prevBtn = root.querySelector("[data-fb-prev]");
  const nextBtn = root.querySelector("[data-fb-next]");
  const counterEl = root.querySelector("[data-fb-counter]");
  const progressEl = root.querySelector("[data-fb-progress]");
  const fillEl = root.querySelector("[data-fb-fill]");
  const zoomBtn = root.querySelector("[data-fb-zoom]");
  const ctaEl = root.querySelector("[data-fb-cta]");

  let pageFlip = null;
  let images = [];
  // Vrai pendant l'ouverture automatique de la couverture, pour ne pas
  // compter ces tours de page comme des gestes de l'utilisateur.
  let autoOpening = false;

  // ── Mesure ────────────────────────────────
  function track(event, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...params });
  }

  // ── Construction du DOM des pages ─────────
  // Les couvertures sont `hard` : StPageFlip les rend rigides comme
  // une vraie couverture cartonnée.
  function buildPages() {
    const frag = document.createDocumentFragment();
    for (let i = 0; i < TOTAL_PAGES; i++) {
      const page = document.createElement("div");
      page.className = "msb-fb__page";
      if (i === 0 || i === TOTAL_PAGES - 1) page.dataset.density = "hard";

      const img = document.createElement("img");
      img.src = BLANK;
      img.dataset.src = pageUrl(i);
      img.alt = `Page ${i + 1} of a Facebook Social Book`;
      img.draggable = false;

      page.appendChild(img);
      frag.appendChild(page);
      images.push(img);
    }
    bookEl.appendChild(frag);
  }

  // Pose le `src` réel sur la fenêtre autour de `center`, et le retire
  // au-delà pour que la mémoire ne monte pas sur les 64 pages.
  function loadWindow(center) {
    const from = Math.max(0, center - WINDOW_BEHIND);
    const to = Math.min(TOTAL_PAGES - 1, center + WINDOW_AHEAD);
    images.forEach((img, i) => {
      const wanted = i >= from && i <= to ? img.dataset.src : BLANK;
      if (img.getAttribute("src") !== wanted) img.setAttribute("src", wanted);
    });
  }

  // ── Compteur ──────────────────────────────
  // StPageFlip est 0-indexé et `showCover` décale la lecture :
  // - portrait (mobile)   : une page affichée      → current + 1
  // - paysage (desktop)   : couverture seule       → 1
  //                         quatrième de couv.     → total
  //                         double page du milieu  → current + 2
  function computeDisplayedPage(current, total, isPortrait) {
    if (isPortrait) return current + 1;
    if (current === 0) return 1;
    if (current === total - 1) return total;
    return current + 2;
  }

  function updateUI() {
    const current = pageFlip.getCurrentPageIndex();
    const total = pageFlip.getPageCount();
    const isPortrait = pageFlip.getOrientation() === "portrait";
    const displayed = computeDisplayedPage(current, total, isPortrait);

    counterEl.textContent = `${displayed} / ${total}`;
    progressEl.setAttribute("aria-valuenow", String(displayed));
    fillEl.style.width = `${(total > 1 ? current / (total - 1) : 0) * 100}%`;

    prevBtn.disabled = current === 0;
    nextBtn.disabled = current >= total - 1;

    loadWindow(current);
  }

  // ── Zoom / lightbox ───────────────────────
  const pswpItems = Array.from({ length: TOTAL_PAGES }, (_, i) => ({
    src: pageUrl(i),
    width: IMAGE_WIDTH,
    height: IMAGE_HEIGHT,
    alt: `Page ${i + 1}`,
  }));

  async function openLightbox(index) {
    const { PhotoSwipeLightbox, PhotoSwipe } = await loadPhotoSwipe(libPswpCoreUrl, libPswpLightboxUrl);
    const lightbox = new PhotoSwipeLightbox({
      dataSource: pswpItems,
      index,
      showHideAnimationType: "fade",
      bgOpacity: 0.95,
      pswpModule: PhotoSwipe,
    });
    lightbox.init();
    lightbox.loadAndOpen(index);
    track("flipbook_zoom", { flipbook_page: index + 1 });
  }

  // Quelle page ouvrir en grand : celle sous le doigt en double page,
  // la page courante en portrait.
  function targetIndexFromClientX(clientX) {
    const current = pageFlip.getCurrentPageIndex();
    if (pageFlip.getOrientation() === "portrait") return current;
    if (current === 0) return 0;
    if (current === TOTAL_PAGES - 1) return TOTAL_PAGES - 1;
    const rect = bookEl.getBoundingClientRect();
    return clientX - rect.left > rect.width / 2 ? current + 1 : current;
  }

  // ── Ouverture différée ────────────────────
  let opened = false;

  async function open() {
    if (opened) return;
    opened = true;
    root.classList.add("is-open");
    track("flipbook_open", { flipbook_book: "parkers-inside-with-cover01" });

    const St = await loadPageFlip(libPageFlipUrl);
    // Levé avant tout le reste : StPageFlip émet un premier `flip` à l'index 0
    // dès le chargement des pages, qui ne doit pas compter comme un geste.
    autoOpening = true;
    buildPages();

    pageFlip = new St.PageFlip(bookEl, {
      width: 460,
      height: 575,
      size: "stretch",
      minWidth: 280,
      maxWidth: 460,
      minHeight: 350,
      maxHeight: 575,
      drawShadow: true,
      flippingTime: 900,
      usePortrait: true,
      autoSize: true,
      maxShadowOpacity: 0.5,
      showCover: true,
      mobileScrollSupport: false,
    });

    // Deux filtres avant de pousser `flipbook_flip` :
    //  - StPageFlip émet plusieurs `flip` pour un seul tour de page ;
    //  - l'ouverture automatique de la couverture n'est pas un geste de
    //    l'utilisateur et ne doit pas compter comme une page tournée.
    let lastTracked = -1;
    pageFlip.on("flip", (e) => {
      updateUI();
      if (autoOpening || e.data === lastTracked) return;
      lastTracked = e.data;
      track("flipbook_flip", { flipbook_page: e.data + 1 });
    });

    // La couverture est posée avant le chargement pour être déjà peinte.
    loadWindow(0);
    pageFlip.loadFromHTML(bookEl.querySelectorAll(".msb-fb__page"));

    // L'événement `init` de StPageFlip ne se déclenche pas de façon fiable en
    // mode HTML : on synchronise l'interface sur un temporisateur.
    setTimeout(updateUI, 50);
    autoOpenCover();

    wireControls();
    window.msbFlipbook = pageFlip; // débogage
  }

  // Ouverture de la couverture. Le geste montre que le livre se tourne
  // vraiment — mais StPageFlip ignore un `flipNext` lancé avant d'avoir
  // mesuré ses pages, d'où l'attente des deux premières images puis une
  // seule nouvelle tentative si rien n'a bougé.
  function autoOpenCover() {
    const ready = Promise.all(
      images.slice(0, 2).map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((res) => {
              img.addEventListener("load", res, { once: true });
              img.addEventListener("error", res, { once: true });
            })
      )
    );

    ready.then(() => {
      setTimeout(() => {
        pageFlip.flipNext();
        // La vérification attend plus longtemps que `flippingTime` (900 ms) :
        // sinon l'animation est encore en cours, l'index vaut toujours 0 et
        // on tournerait une deuxième page pour rien.
        setTimeout(() => {
          if (pageFlip.getCurrentPageIndex() === 0) pageFlip.flipNext();
          // À partir d'ici, tout tour de page vient de l'utilisateur.
          setTimeout(() => { autoOpening = false; }, 1400);
        }, 1400);
      }, 500);
    });
  }

  function wireControls() {
    prevBtn.addEventListener("click", () => pageFlip.flipPrev());
    nextBtn.addEventListener("click", () => pageFlip.flipNext());

    const jumpToRatio = (ratio) => {
      const total = pageFlip.getPageCount();
      const clamped = Math.max(0, Math.min(1, ratio));
      pageFlip.flip(Math.round(clamped * (total - 1)));
    };
    const ratioFromEvent = (e) => {
      const rect = progressEl.getBoundingClientRect();
      return (e.clientX - rect.left) / rect.width;
    };

    progressEl.addEventListener("click", (e) => jumpToRatio(ratioFromEvent(e)));

    let dragging = false;
    progressEl.addEventListener("pointerdown", (e) => {
      dragging = true;
      progressEl.setPointerCapture(e.pointerId);
    });
    progressEl.addEventListener("pointermove", (e) => {
      if (dragging) jumpToRatio(ratioFromEvent(e));
    });
    const stopDrag = () => { dragging = false; };
    progressEl.addEventListener("pointerup", stopDrag);
    progressEl.addEventListener("pointercancel", stopDrag);

    zoomBtn.addEventListener("click", () => {
      const current = pageFlip.getCurrentPageIndex();
      const isPortrait = pageFlip.getOrientation() === "portrait";
      let index;
      if (isPortrait) index = current;
      else if (current === 0) index = 0;
      else if (current === TOTAL_PAGES - 1) index = TOTAL_PAGES - 1;
      else index = Math.min(current + 1, TOTAL_PAGES - 1);
      openLightbox(index);
    });

    bookEl.addEventListener("dblclick", (e) => {
      e.preventDefault();
      openLightbox(targetIndexFromClientX(e.clientX));
    });

    // Double-tap mobile : PhotoSwipe ne le fait pas pour nous ici.
    let lastTap = 0;
    bookEl.addEventListener("touchend", (e) => {
      const now = Date.now();
      if (now - lastTap < 350 && e.changedTouches.length) {
        e.preventDefault();
        openLightbox(targetIndexFromClientX(e.changedTouches[0].clientX));
        lastTap = 0;
      } else {
        lastTap = now;
      }
    }, { passive: false });

    // Clavier : uniquement quand le livre a le focus ou qu'on est dans
    // la section — la page hôte a ses propres raccourcis.
    root.addEventListener("keydown", (e) => {
      if (e.target.matches("input, textarea, [contenteditable]")) return;
      const total = pageFlip.getPageCount();
      switch (e.key) {
        case "ArrowRight": e.preventDefault(); pageFlip.flipNext(); break;
        case "ArrowLeft":  e.preventDefault(); pageFlip.flipPrev(); break;
        case "Home":       e.preventDefault(); pageFlip.flip(0); break;
        case "End":        e.preventDefault(); pageFlip.flip(total - 1); break;
      }
    });
  }

  teaser.addEventListener("click", open);

  // Préchauffage : dès que la section approche du viewport, on va chercher
  // StPageFlip (44 Ko). L'utilisateur qui clique ensuite n'attend plus le
  // réseau, et celui qui ne scrolle jamais jusqu'ici ne paie rien.
  if ("IntersectionObserver" in window) {
    const warm = new IntersectionObserver((entries, obs) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      obs.disconnect();
      loadPageFlip(libPageFlipUrl).catch(() => {});
    }, { rootMargin: "600px" });
    warm.observe(root);
  }

  if (ctaEl) {
    ctaEl.addEventListener("click", () => {
      track("flipbook_cta_click", {
        flipbook_opened: opened,
        flipbook_page: pageFlip ? pageFlip.getCurrentPageIndex() + 1 : 0,
      });
    });
  }
}
